/**
 * Design System Component: Autocomplete
 */
import {
  AutocompleteProps as MuiAutocompleteProps,
  ChipTypeMap,
  TextFieldProps,
  AutocompleteRenderInputParams,
} from "@mui/material";
import AutocompleteTextField from "./components/AutocompleteTextField";
import StyledAutocomplete from "./components/StyledAutocomplete";
import SearchIcon from "components/Icons/SearchIcon";
import { useCallback, useMemo } from "react";
import CaretIcon from "components/Icons/CaretIcon";
import AutocompleteNoOptionsText from "./components/AutocompleteNoOptionsText";

export interface AutocompleteProps<
  Value,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
  ChipComponent extends React.ElementType = ChipTypeMap["defaultComponent"],
  BaseAutocompleteProps extends MuiAutocompleteProps<Value, Multiple, DisableClearable, FreeSolo, ChipComponent> =
    MuiAutocompleteProps<Value, Multiple, DisableClearable, FreeSolo, ChipComponent>,
>
  extends
    Pick<
      BaseAutocompleteProps,
      | "options"
      | "renderOption"
      | "disableClearable"
      | "multiple"
      | "getOptionLabel"
      | "getOptionKey"
      | "getOptionDisabled"
      | "isOptionEqualToValue"
      | "groupBy"
      | "limitTags"
      | "filterSelectedOptions"
      | "renderTags"
      | "renderValue"
      | "ChipProps"
      | "sx"
      | "open"
      | "onOpen"
      | "onClose"
      | "inputValue"
      | "onInputChange"
      | "blurOnSelect"
      | "disabled"
      | "disableCloseOnSelect"
      | "slotProps"
      | "filterOptions"
      | "renderGroup"
      | "freeSolo"
      | "autoHighlight"
    >,
    Required<Pick<BaseAutocompleteProps, "value" | "onChange">> {
  TextFieldProps?: Omit<TextFieldProps, "InputLabelProps" | "value" | "onChange"> & {
    variant?: Exclude<TextFieldProps["variant"], "filled">;
  };
  label?: string;
  placeholder?: string;
  showSearchIcon?: boolean;
  renderInputAdornment?: (
    value?: MuiAutocompleteProps<Value, Multiple, DisableClearable, FreeSolo, ChipComponent>["value"]
  ) => React.ReactNode;
}

/**
 * @example
  <Autocomplete
    multiple
    value={option}
    onChange={(_, value) => setOption(value)}
    options={options}
    label="Standard Option"
    placeholder="Std placeholder"
  />
 */

function Autocomplete<
  Value,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
>(props: AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo>) {
  const { TextFieldProps, label, placeholder, renderInputAdornment, showSearchIcon, ...rest } = props;

  const startAdornment = useMemo(
    () =>
      showSearchIcon ? (
        <SearchIcon sx={{ ml: 0.5, mr: 0.5, color: "grey.100" }} fontSize="small" />
      ) : (
        renderInputAdornment && renderInputAdornment(props.value)
      ),
    [showSearchIcon, props.value, renderInputAdornment]
  );

  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <AutocompleteTextField
        {...params}
        {...TextFieldProps}
        size={TextFieldProps?.size ?? "small"}
        label={label}
        placeholder={!rest.open && Array.isArray(rest.value) && rest.value.length ? undefined : placeholder}
        slotProps={{ inputLabel: { shrink: true } }}
        inputProps={{ ...params.inputProps, ...TextFieldProps?.inputProps }}
        InputProps={{
          ...props.TextFieldProps?.InputProps,
          ...params.InputProps,
          startAdornment: params.InputProps.startAdornment || startAdornment,
          endAdornment: (
            <>
              {props.TextFieldProps?.InputProps?.endAdornment}
              {params.InputProps.endAdornment}
            </>
          ),
        }}
      />
    ),
    [TextFieldProps, label, placeholder, props.TextFieldProps?.InputProps, rest.open, rest.value, startAdornment]
  );

  return (
    <StyledAutocomplete<Value, Multiple, DisableClearable, FreeSolo>
      {...rest}
      filterSelectedOptions={rest.filterSelectedOptions ?? true}
      fullWidth
      popupIcon={<CaretIcon color="primary" sx={{ fontSize: "16px" }} />}
      slotProps={{ ...rest.slotProps, chip: { variant: "outlined", ...rest.slotProps?.chip } }}
      renderInput={renderInput}
      noOptionsText={<AutocompleteNoOptionsText />}
    />
  );
}
export default Autocomplete;
